import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Transaction } from '@/types/Transaction';
import {
  ArrowDownRight,
  ArrowUpRight,
  ArrowRight,
  Search,
} from 'lucide-react';

export const DashboardTransactions: React.FC<{ transactions: Transaction[] }> = ({ transactions }) => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState<'7d' | '30d' | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | 'income' | 'expense'>('all');

  const handleNavigateWithAnimation = () => {
    if (containerRef.current) {
      containerRef.current.classList.add('fade-out');
      setTimeout(() => navigate('/transactions'), 250); // match CSS animation
    } else {
      navigate('/transactions');
    }
  };

  const filteredTransactions = useMemo(() => {
    const now = new Date();
    const rangeDate = (days: number) =>
      new Date(now.getFullYear(), now.getMonth(), now.getDate() - days);

    return transactions
      .filter((tx) => {
        if (dateFilter === 'all') return true;
        const txDate = new Date(tx.date);
        return txDate >= rangeDate(dateFilter === '7d' ? 7 : 30);
      })
      .filter((tx) =>
        tx.merchantName?.toLowerCase().includes(search.toLowerCase())
      )
      .filter((tx) => {
        if (typeFilter === 'income') return tx.amount > 0;
        if (typeFilter === 'expense') return tx.amount < 0;
        return true;
      });
  }, [transactions, search, dateFilter, typeFilter]);

  const handleMerchantClick = (merchantName: string | null) => {
    if (merchantName) {
      navigate(`/transactions/merchant-summary/${encodeURIComponent(merchantName)}`);
    }
  };

  return (
    <div
      ref={containerRef}
      className="bg-white dark:bg-gray-900 p-6 rounded-xl shadow border dark:border-gray-700 transition-all duration-300"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <h2
          onClick={handleNavigateWithAnimation}
          className="text-xl font-bold text-gray-800 dark:text-white mb-1 flex items-center gap-1 cursor-pointer hover:text-blue-600 transition-colors"
        >
          Recent Transactions
          <ArrowRight className="w-4 h-4" />
        </h2>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search merchant..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 pr-4 py-2 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-white"
            />
          </div>
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value as any)}
            className="px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-white"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="all">All time</option>
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-4">
        {['all', 'income', 'expense'].map((type) => {
          const label =
            type === 'all' ? 'All' : type === 'income' ? 'Income' : 'Expenses';
          const isActive = typeFilter === type;
          return (
            <button
              key={type}
              onClick={() => setTypeFilter(type as any)}
              className={`text-sm font-medium px-4 py-2 rounded-md transition-all ${
                isActive
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* Transactions List */}
      <ul className="divide-y divide-gray-200 dark:divide-gray-700">
        {filteredTransactions.map((tx) => {
          const isExpense = tx.amount < 0;
          const amountClass = isExpense
            ? 'text-red-600 bg-red-100 dark:bg-red-900/20'
            : 'text-green-600 bg-green-100 dark:bg-green-900/20';
          const Icon = isExpense ? ArrowDownRight : ArrowUpRight;

          return (
            <li
              key={tx.id}
              className="flex items-center justify-between py-3 hover:bg-gray-50 dark:hover:bg-gray-800 px-2 rounded transition cursor-pointer"
              onClick={() => handleMerchantClick(tx.merchantName)}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-800">
                  <Icon className={`w-5 h-5 ${isExpense ? 'text-red-500' : 'text-green-500'}`} />
                </div>
                <div className="text-sm">
                  <p className="text-gray-800 dark:text-white font-medium">
                    {tx.merchantName ?? 'N/A'}
                  </p>
                  <p className="text-gray-500 text-xs dark:text-gray-400">{tx.date}</p>
                </div>
              </div>

              <div className={`text-sm px-3 py-1 rounded-full font-semibold ${amountClass}`}>
                {isExpense ? '-' : '+'}${Math.abs(tx.amount).toFixed(2)}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
